import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';

interface SaveButtonProps {
  onSave: () => Promise<boolean>; // Returns true if save was successful
  disabled?: boolean;
  className?: string;
}

export default function SaveButton({ onSave, disabled = false, className = '' }: SaveButtonProps) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    if (showSaved) {
      const timer = setTimeout(() => setShowSaved(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showSaved]);

  const handleClick = async () => {
    setIsSaving(true);
    try {
      const success = await onSave();
      if (success) {
        setShowSaved(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Button
      className={className}
      onClick={handleClick}
      disabled={disabled || showSaved}
      isLoading={isSaving}
      variant={showSaved ? 'secondary' : 'primary'}
    >
      <AnimatePresence mode="wait">
        {showSaved ? (
          <motion.span
            key="saved"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2"
          >
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t('common.saved')}
          </motion.span>
        ) : (
          <motion.span
            key="save"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {t('common.save')}
          </motion.span>
        )}
      </AnimatePresence>
    </Button>
  );
}
